import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeenIcon } from '@/components';
import { IPOICategory } from '@/types/poi-management.types';

interface CategoryFilterProps {
  categories: IPOICategory[];
  selectedCategories: string[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<string[]>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategories,
  setSelectedCategories,
  isOpen,
  setIsOpen,
  isLoading = false,
}) => {
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncViewport = () => {
      setIsMobileViewport(window.innerWidth < 640);
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);

    return () => {
      window.removeEventListener('resize', syncViewport);
    };
  }, []);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !containerRef.current?.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Handle Escape key to close dropdown (WCAG)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, setIsOpen]);

  // Focus management and focus trap (WCAG)
  useEffect(() => {
    if (isOpen && firstFocusableRef.current) {
      // Auto-focus the first interactive element when dropdown opens
      firstFocusableRef.current.focus();

      // Focus trap: keep focus within dropdown
      const handleFocusTrap = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && panelRef.current) {
          const focusableElements = panelRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleFocusTrap);

      return () => {
        document.removeEventListener('keydown', handleFocusTrap);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMobileViewport || typeof document === 'undefined') return;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isMobileViewport, isOpen]);

  const handleCategoryToggle = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
    } else {
      setSelectedCategories((prev) => [...prev, categoryId]);
    }
  };

  const handleSubtypeToggle = (subtypeId: string) => {
    if (selectedCategories.includes(subtypeId)) {
      setSelectedCategories((prev) => prev.filter((id) => id !== subtypeId));
    } else {
      setSelectedCategories((prev) => [...prev, subtypeId]);
    }
  };

  const handleClearAll = () => {
    setSelectedCategories([]);
    setIsOpen(false);
  };

  const handleApply = () => {
    setIsOpen(false);
  };

  const panelContent = (isMobile = false) => (
    <div
      ref={panelRef}
      className={
        isMobile
          ? 'relative z-[1000] flex w-full max-h-[80vh] flex-col overflow-hidden rounded-t-2xl bg-gray-100 shadow-2xl'
          : 'absolute top-full left-0 mt-2 w-full max-w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto rounded-lg border border-gray-200 bg-gray-100 shadow-lg sm:left-auto sm:right-0 sm:w-96 z-[60] max-h-96'
      }
      role="dialog"
      aria-modal={isMobile ? 'true' : 'false'}
      aria-labelledby="category-filter-title"
    >
      <div className={isMobile ? 'flex min-h-0 flex-1 flex-col p-4' : 'p-4'}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3
              id="category-filter-title"
              className="text-sm font-medium text-gray-900"
            >
              Select Categories
            </h3>
            {isMobile && (
              <p className="mt-1 text-xs text-gray-500">
                Choose one or more categories to filter the POI list.
              </p>
            )}
          </div>
          {selectedCategories.length > 0 && (
            <button
              ref={firstFocusableRef}
              onClick={handleClearAll}
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
              aria-label="Clear all selected categories"
            >
              Clear All
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="py-4 text-center text-sm text-gray-500">
            Loading categories...
          </div>
        ) : categories.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">
            No categories available
          </div>
        ) : (
          <div
            className={
              isMobile
                ? 'min-h-0 flex-1 space-y-2 overflow-y-auto pb-1'
                : 'space-y-2 max-h-64 overflow-y-auto'
            }
          >
            {categories.map((category) => (
              <div key={category.id} className="space-y-1">
                <label className="flex cursor-pointer items-center space-x-2 rounded p-1 hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                    className="cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="cursor-pointer text-sm font-medium text-gray-900">
                    {category.label}
                  </span>
                  <span className="cursor-pointer text-xs text-gray-500">
                    ({category.subtypes.length})
                  </span>
                </label>

                {category.subtypes.map((subtype) => (
                  <label
                    key={subtype.id}
                    className="ml-6 flex cursor-pointer items-center space-x-2 rounded p-1 hover:bg-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(subtype.id)}
                      onChange={() => handleSubtypeToggle(subtype.id)}
                      className="cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">
                      {subtype.label}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end space-x-2 border-t border-gray-100 pt-3">
          {isMobile && (
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="btn btn-sm btn-light"
              aria-label="Cancel category filter"
            >
              Cancel
            </button>
          )}
          <button
            ref={
              selectedCategories.length === 0 ? firstFocusableRef : undefined
            }
            onClick={handleClearAll}
            className="btn btn-sm btn-light"
            disabled={selectedCategories.length === 0}
            aria-label="Clear all filters"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="btn btn-sm btn-primary"
            aria-label="Apply selected filters"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative w-full flex-shrink-0 sm:w-auto" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-sm btn-light flex h-8 w-full items-center justify-between gap-2 sm:h-10 sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Filter by category"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* <KeenIcon type="solid" icon="filter" className="text-gray-600" /> */}
        Filter by Category
        <KeenIcon
          type="solid"
          icon={isOpen ? 'up' : 'down'}
          className="text-gray-600"
        />
      </button>

      {isOpen && !isMobileViewport && panelContent()}
      {isOpen &&
        isMobileViewport &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[999] flex items-end bg-black/40 p-0">
            <div
              className="absolute inset-0"
              aria-hidden="true"
              onClick={() => setIsOpen(false)}
            />
            {panelContent(true)}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CategoryFilter;
